import {useState} from "react";
import {BuildingReward, GrantKinds, GrantSpec, TriggerKinds, TriggerSpec} from "../../models/remoteConstants.model";
import {useApp} from "../../app.context";
import {useIntegrator} from "../../integrator.context";
import Collapsible from "../../elements/collapsible";
import NumberInputLabel from "../../elements/numberInputLabel.element";
import SelectableLabel from "../../elements/selectable.label";
import BooleanInput from "../../elements/booleanInput.element";
import IconButton from "../../elements/IconButton";
import {UpdateBuildingPassiveRewardCommand} from "../../commands/updateBuildingPassiveReward.command";
import {RemoveBuildingPassiveRewardCommand} from "../../commands/removeBuildingPassiveReward.command";
import RewardTimelineDialog from "./rewardTimelineDialog";
import {defaultTriggerSpec} from "../../utils/reward.utils";

// Local-only replacement for SelectableLabel that does NOT run option strings
// through getLocalizationValue. Used for fixed enum dropdowns (trigger kind,
// turn phase, grant kind) whose option names happen to collide with entity
// IDs in some branches' localization tables (e.g. "Gold" / "Population").
function RawSelectLabel(props: { name: string; value: string; options: readonly string[]; onChange: (v: string) => void }) {
    return (
        <div className={'layout horizontal center stretch'}>
            <div className={'layout horizontal center gap'}>
                <p>{props.name}</p>
            </div>
            <select style={{width: 200}} value={props.value}
                    onChange={e => props.onChange(e.target.value)}>
                {props.options.map((o, i) => (
                    <option key={i} value={o}>{o}</option>
                ))}
            </select>
        </div>
    );
}

interface PassiveRewardCardProps {
    buildingId: string;
    reward: BuildingReward;
    // Set on a card the user has just created, so its defaults are visible
    // and editable without a second click.
    defaultOpen?: boolean;
}

function summarize(r: BuildingReward, resolveName: (id: string) => string): string {
    const t = r.trigger;
    const grant = `+${r.grant.amount} ${r.grant.kind}`;
    const period = (cadence: number) => cadence === 1 ? 'per turn' : `every ${cadence} turns`;
    switch (t.kind) {
        case 'OnPlace': return grant;
        case 'OnUpgrade': return `${grant} on upgrade`;
        case 'EveryNTurns': return `${grant} ${period(t.cadence)}`;
        case 'PerAdjacent': return `${grant} per turn for each adjacent ${resolveName(t.adjacentTo) || '?'}`;
        case 'PerAdjacentOnce': return `${grant} once per adjacent ${resolveName(t.adjacentTo) || '?'}`;
        case 'TilesExplored': return `${grant} per ${t.cloudCount} tile${t.cloudCount === 1 ? '' : 's'} explored`;
        case 'FullTurnOccupied': return `${grant} when fully occupied`;
        case 'BuiltOnTile': return `${grant} ${period(t.cadence)} if built on ${resolveName(t.containing) || '?'}`;
    }
}

function PassiveRewardCard(props: PassiveRewardCardProps) {
    const {state, executeCommand} = useIntegrator();
    const {getLocalizationValue, confirmDelete, getEntityOptions} = useApp();
    const {reward, buildingId} = props;
    const [showTimeline, setShowTimeline] = useState(false);

    const building = state.buildings.find(b => b.id === buildingId);
    const hasCadenceTrigger = reward.trigger.kind === 'EveryNTurns' || reward.trigger.kind === 'BuiltOnTile';

    const update = (mutate: (r: BuildingReward) => void) =>
        executeCommand(new UpdateBuildingPassiveRewardCommand(buildingId, reward.uid, mutate));

    function changeTriggerKind(kind: TriggerSpec['kind']) {
        update(r => { r.trigger = defaultTriggerSpec(kind); });
    }

    function changeGrantKind(kind: GrantSpec['kind']) {
        update(r => { r.grant = {kind, amount: r.grant.amount}; });
    }

    const header = (
        <div className={'layout horizontal'} style={{gap: 10, alignItems: 'center', flexGrow: 1}}>
            <BooleanInput value={reward.active} onChange={(v) => update(r => { r.active = v; })}/>
            <p style={{flexGrow: 1}}>{summarize(reward, getLocalizationValue)}</p>
            {hasCadenceTrigger && building && <IconButton icon={'calendar'} onClick={(e) => {
                e.stopPropagation();
                setShowTimeline(true);
            }}/>}
            <IconButton icon={'trash'} onClick={async (e) => {
                e.stopPropagation();
                if (await confirmDelete(summarize(reward, getLocalizationValue), 'reward')) {
                    executeCommand(new RemoveBuildingPassiveRewardCommand(buildingId, reward.uid));
                }
            }}/>
        </div>
    );

    return (
        <div className={'box'} style={{padding: '8px 12px', opacity: reward.active ? 1 : 0.5}}>
            <Collapsible title={header} defaultOpen={props.defaultOpen ?? false}>
                <div className={'layout vertical gap'} style={{gap: 6, marginTop: 8}}>
                    <RawSelectLabel name={'Trigger'} value={reward.trigger.kind}
                                    options={TriggerKinds}
                                    onChange={(v) => changeTriggerKind(v as TriggerSpec['kind'])}/>

                    {reward.trigger.kind === 'EveryNTurns' && <>
                        <NumberInputLabel title={'Cadence (turns)'} value={reward.trigger.cadence}
                                          onChange={(v) => update(r => { if (r.trigger.kind === 'EveryNTurns') r.trigger.cadence = v; })}/>
                        <NumberInputLabel title={'First trigger delay'} value={reward.trigger.firstTriggerDelay}
                                          onChange={(v) => update(r => { if (r.trigger.kind === 'EveryNTurns') r.trigger.firstTriggerDelay = v; })}/>
                    </>}

                    {reward.trigger.kind === 'PerAdjacent' && <>
                        <SelectableLabel name={'Adjacent to'} value={reward.trigger.adjacentTo}
                                         groups={getEntityOptions()}
                                         onChange={(v) => update(r => { if (r.trigger.kind === 'PerAdjacent') r.trigger.adjacentTo = String(v); })}/>
                    </>}

                    {reward.trigger.kind === 'PerAdjacentOnce' && <>
                        <SelectableLabel name={'Adjacent to'} value={reward.trigger.adjacentTo}
                                         groups={getEntityOptions()}
                                         onChange={(v) => update(r => { if (r.trigger.kind === 'PerAdjacentOnce') r.trigger.adjacentTo = String(v); })}/>
                    </>}

                    {reward.trigger.kind === 'TilesExplored' && <>
                        <NumberInputLabel title={'Tiles required'} value={reward.trigger.cloudCount}
                                          onChange={(v) => update(r => { if (r.trigger.kind === 'TilesExplored') r.trigger.cloudCount = v; })}/>
                    </>}

                    {reward.trigger.kind === 'BuiltOnTile' && <>
                        <SelectableLabel name={'Tile contains'} value={reward.trigger.containing}
                                         groups={getEntityOptions()}
                                         onChange={(v) => update(r => { if (r.trigger.kind === 'BuiltOnTile') r.trigger.containing = String(v); })}/>
                        <NumberInputLabel title={'Cadence (turns)'} value={reward.trigger.cadence}
                                          onChange={(v) => update(r => { if (r.trigger.kind === 'BuiltOnTile') r.trigger.cadence = v; })}/>
                        <NumberInputLabel title={'First trigger delay'} value={reward.trigger.firstTriggerDelay}
                                          onChange={(v) => update(r => { if (r.trigger.kind === 'BuiltOnTile') r.trigger.firstTriggerDelay = v; })}/>
                    </>}

                    <RawSelectLabel name={'Grant'} value={reward.grant.kind}
                                    options={GrantKinds}
                                    onChange={(v) => changeGrantKind(v as GrantSpec['kind'])}/>
                    <NumberInputLabel title={'Amount'} value={reward.grant.amount}
                                      onChange={(v) => update(r => { r.grant.amount = v; })}/>
                    {reward.trigger.kind !== 'OnPlace' && reward.trigger.kind !== 'OnUpgrade' && (
                        <NumberInputLabel title={'Amount increase per trigger'} value={reward.amountIncreasePerTrigger}
                                          onChange={(v) => update(r => { r.amountIncreasePerTrigger = v; })}/>
                    )}
                </div>
            </Collapsible>
            {showTimeline && building && (
                <RewardTimelineDialog reward={reward} building={building}
                                      title={summarize(reward, getLocalizationValue)}
                                      onClose={() => setShowTimeline(false)}/>
            )}
        </div>
    );
}

export default PassiveRewardCard;
